<template>
  <div class="card employee-card">
    <div class="card__section">
      <h5 class="org-name text-blue text-upper text-small">
        <span class="text-small">{{ employee.organizationRef.name }}</span>
        <br />
        <span v-if="employee.subOrganizationRef" class="sub-org">
          {{ employee.subOrganizationRef.name }}
        </span>
      </h5>

      <p class="text-small">
        <strong>Grant Limit:</strong>
        {{ employee.organizationRef.grantLimit | currency }}
      </p>
      <p class="text-small" style="margin-top: -0.5rem">
        <strong>Grant Limit Duration:</strong>
        {{ formatGrantLimitDuration(employee.organizationRef.grantLimitDuration, employee.organizationRef.rollingGrantDuration) }}
      </p>
      <p class="text-small" style="margin-top: -0.5rem">
        <strong>Lookback Period:</strong>
        {{ customVariablesEight }}
      </p>
      <h1 class="employee-name" @click="onNameClick">
        {{ (employee.fullName && employee.fullName.trim()) || 'Unnamed User' }}
        <span class="inherit-font asterisk">
          {{ areChangedFields(['first_name', 'last_name']) }}</span
        >
      </h1>
      <p class="text-small">
        <md-icon style="color: gray">email</md-icon>
        {{ (employee.email && employee.email.trim()) || 'No email' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['email']) }}</span>
      </p>
      <p class="text-small">
        <md-icon style="color: gray">person</md-icon>
        {{ (employee.employeeCodeRef && employee.employeeCodeRef.code) || 'No Employee Code' }}
      </p>
      <p class="text-small">
        <md-icon style="color: gray">attach_money</md-icon>
        {{ (employee.paypalEmail && employee.paypalEmail.trim()) || 'No Paypal Email' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['paypal_email']) }}</span>
      </p>
      <p class="text-small">
        <md-icon style="color: gray">smartphone</md-icon>
        {{ (employee.phoneNumber && employee.phoneNumber.trim()) || 'No Phone Number' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['phone_number']) }}</span>
      </p>
      <p class="text-small">
        <md-icon style="color: gray">place</md-icon>
        {{ (employee.zipCode && employee.zipCode.trim()) || 'No Zip Code' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['zip_code']) }}</span>
      </p>
      <p class="text-small">
        <md-icon style="color: gray">cake</md-icon>
        {{ employee.birthYear || 'No Birth Date' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['birth_year']) }}</span>
      </p>
      <p class="text-small">
        <strong>Language:</strong>
        {{ employee.preferredLanguage === 'es_ES' ? 'Spanish' : 'English' }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['preferred_language']) }}</span>
      </p>
      <p class="text-small">
        <strong>Preferred channel:</strong>
        {{ channel }}
        <span class="inherit-font asterisk">
          {{ areChangedFields(['enable_sms_alerts', 'enable_email_alerts']) }}</span
        >
      </p>
      <p class="text-small">
        <strong>Can we call?</strong>
        {{
          employee.enablePhoneContact ? 'Yes' : employee.hasSetNotifPreferences ? 'No' : 'Unknown'
        }}
        <span class="inherit-font asterisk"> {{ areChangedFields(['enable_phone_contact']) }}</span>
      </p>
    </div>

    <div class="card__section card__section--bordered">
      <h5 class="text-blue text-upper text-small">
        {{ employee.firstName.trim() || $gettext('Unnamed User') }}'s {{ $gettext('HISTORY') }}
      </h5>
      <p class="text-small">
        <strong>Total Requests Submitted (all time):</strong>
        {{ employee.countRequestsSubmitted }}
      </p>
      <p class="text-small">
        <strong>Requests approved since last limit reset:</strong>
        {{ employee.countRequestsApprovedSinceLimitReset }}
      </p>
      <p class="text-small">
        <strong>Total Disbursed (all time):</strong>
        {{ employee.totalPayouts | currency }}
      </p>
      <p class="text-small">
        <strong>Total Contributed (all time):</strong>
        {{ (employee.totalContributions || 0) | currency }}
      </p>
      <p class="text-small">
        <strong>Total Disbursed Since Last Limit Reset:</strong>
        {{ employee.totalPayoutsSinceLastGrantLimitReset | currency }}
      </p>
    </div>
  </div>
</template>

<script>
import { customVariablesFilter } from '../../services/organizationCustomVariables'
import { mapState } from 'vuex'

export default {
  name: 'EmployeeCard',
  props: {
    employee: {
      type: Object,
      required: true,
    },
  },
  methods: {
    onNameClick(employee) {
      const routeData = this.$router.resolve({
        name: 'StaffEmployeeDetail',
        params: {
          id: this.employee.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    areChangedFields(field_names) {
      const fieldChanged = field_names.some((name) => {
        return this.employee.changedFields.includes(name)
      })
      if (fieldChanged) {
        return '*'
      }
    },
    formatGrantLimitDuration(duration, rollingGrantDuration) {
      const lookup = {
        CALENDAR_YEAR: 'Calendar Year',
        LIFETIME: 'Lifetime',
        ROLLING_X_YEARS: `Rolling ${rollingGrantDuration} Years`,
      }
      return lookup[duration]
    }
  },
  computed: {
    ...mapState(['organizationDetails']),
    customVariablesEight() {
      return customVariablesFilter(
        '{{cv8}}',
        this.employee.organizationRef.customVariables || {},
        'en_US',
      )
    },
    channel() {
      let channel = 'Email'
      if (this.employee.enableSmsAlerts && this.employee.validatedNumber) {
        channel = 'SMS'
      }
      if (
        this.employee.enableSmsAlerts &&
        this.employee.validatedNumber &&
        this.employee.enableEmailAlerts
      ) {
        channel = 'Email & SMS'
      }
      return channel
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/cards';

.card {
  @include card;
}

.employee-card {
  flex: 5;
  align-self: flex-start;

  @media (min-width: $mobile-break-point) {
    min-width: 24rem;
  }
}

.org-name {
  line-height: 0.65rem;
}
.sub-org {
  font-size: 0.65rem;
  opacity: 0.75;
}

.asterisk {
  color: $ruby;
}

.employee-name {
  text-align: left;
  cursor: pointer;
}
</style>
